import { useEffect, useState } from "react";
import { format, isToday, isTomorrow, parseISO, isPast } from "date-fns";
import { es } from "date-fns/locale";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle2, Clock, ListTodo, Plus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { useAuth } from "@/contexts/AuthContext";
import { useWorkspace } from "@/hooks/useWorkspace";

interface Task {
  id: string;
  title: string;
  description: string | null;
  due_date: string | null;
  due_time: string | null;
  priority: string;
  status: string;
}

const priorityColors: Record<string, string> = {
  low: "bg-muted text-muted-foreground border-muted",
  medium: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
  high: "bg-destructive/10 text-destructive border-destructive/20",
};

const priorityLabels: Record<string, string> = {
  low: "Baja",
  medium: "Media",
  high: "Alta",
};

export function UpcomingTasks() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newTask, setNewTask] = useState({ title: "", due_date: "", priority: "medium" });
  const [saving, setSaving] = useState(false);
  const { workspace } = useWorkspace();
  const workspaceId = workspace?.id || null;

  useEffect(() => {
    if (workspaceId) {
      fetchTasks();
      const channel = supabase
        .channel(`tasks-changes-${workspaceId}`)
        .on("postgres_changes", { event: "*", schema: "public", table: "tasks", filter: `workspace_id=eq.${workspaceId}` }, () => fetchTasks())
        .subscribe();
      return () => { supabase.removeChannel(channel); };
    }
  }, [workspaceId]);

  const fetchTasks = async () => {
    if (!workspaceId) return;
    try {
      const { data, error } = await supabase
        .from("tasks")
        .select("*")
        .eq("workspace_id", workspaceId)
        .in("status", ["pending", "in_progress"])
        .order("due_date", { ascending: true, nullsFirst: false })
        .order("priority", { ascending: false })
        .limit(5);

      if (error) throw error;
      setTasks(data || []);
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleTaskComplete = async (taskId: string, currentStatus: string) => {
    try {
      const newStatus = currentStatus === "completed" ? "pending" : "completed";
      const { error } = await supabase.from("tasks").update({ status: newStatus }).eq("id", taskId);
      if (error) throw error;
      toast.success(newStatus === "completed" ? "Tarea completada" : "Tarea marcada como pendiente");
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error("Error al actualizar la tarea");
    }
  };

  const handleCreateTask = async () => {
    if (!newTask.title.trim()) { toast.error("El título es requerido"); return; }
    if (!user) { toast.error("Debes iniciar sesión para crear tareas"); return; }
    setSaving(true);
    try {
      const { error } = await supabase.from("tasks").insert({ title: newTask.title, due_date: newTask.due_date || null, priority: newTask.priority, created_by: user.id });
      if (error) throw error;
      toast.success("Tarea creada correctamente");
      setNewTask({ title: "", due_date: "", priority: "medium" });
      setIsDialogOpen(false);
    } catch (error) {
      console.error("Error creating task:", error);
      toast.error("Error al crear la tarea");
    } finally {
      setSaving(false);
    }
  };

  const formatDueDate = (dateStr: string | null) => {
    if (!dateStr) return null;
    const date = parseISO(dateStr);
    if (isToday(date)) return "Hoy";
    if (isTomorrow(date)) return "Mañana";
    return format(date, "d MMM", { locale: es });
  };

  const isOverdue = (dateStr: string | null) => {
    if (!dateStr) return false;
    const date = parseISO(dateStr);
    return isPast(date) && !isToday(date);
  };

  if (loading) {
    return (
      <Card className="animate-fade-in">
        <CardHeader><CardTitle className="flex items-center gap-2"><ListTodo className="h-5 w-5 text-primary" /> Tareas Pendientes</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {[1, 2, 3].map((i) => (<div key={i} className="flex items-center gap-4"><Skeleton className="h-5 w-5 rounded" /><div className="flex-1 space-y-2"><Skeleton className="h-4 w-3/4" /><Skeleton className="h-3 w-1/2" /></div></div>))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="animate-fade-in">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2"><ListTodo className="h-5 w-5 text-primary" /> Tareas Pendientes</CardTitle>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild><Button variant="ghost" size="sm" className="h-8 w-8 p-0"><Plus className="h-4 w-4" /></Button></DialogTrigger>
          <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden">
            <div className="relative bg-gradient-to-r from-primary/15 via-primary/5 to-accent/10 px-5 py-4 border-b">
              <div className="absolute inset-0 bg-grid-white/10 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] pointer-events-none" />
              <div className="relative flex items-center gap-3">
                <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center">
                  <ListTodo className="h-4.5 w-4.5 text-primary" />
                </div>
                <DialogTitle className="text-lg font-bold">Nueva Tarea Rápida</DialogTitle>
              </div>
            </div>
            <div className="space-y-4 px-5 py-4">
              <div className="space-y-2">
                <Label htmlFor="title" className="text-sm font-semibold">Título *</Label>
                <Input id="title" placeholder="¿Qué necesitas hacer?" value={newTask.title} onChange={(e) => setNewTask({ ...newTask, title: e.target.value })} className="h-11 border-2 focus-visible:ring-primary/30" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="due_date" className="text-sm font-semibold">Fecha límite</Label>
                <Input id="due_date" type="date" value={newTask.due_date} onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })} className="h-11" />
              </div>
              <div className="space-y-3">
                <Label className="text-sm font-semibold">Prioridad</Label>
                <div className="grid grid-cols-3 gap-2">
                  {([
                    { value: "low", label: "Baja", emoji: "🟢", activeClass: "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400" },
                    { value: "medium", label: "Media", emoji: "🟡", activeClass: "border-amber-500 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400" },
                    { value: "high", label: "Alta", emoji: "🔴", activeClass: "border-rose-500 bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-400" },
                  ]).map((p) => (
                    <button
                      key={p.value}
                      type="button"
                      onClick={() => setNewTask({ ...newTask, priority: p.value })}
                      className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border-2 font-medium text-sm transition-all duration-200 ${
                        newTask.priority === p.value ? p.activeClass : "border-transparent bg-muted/50 text-muted-foreground hover:bg-muted"
                      }`}
                    >
                      <span>{p.emoji}</span>
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t bg-muted/30">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleCreateTask} disabled={saving} className="min-w-[100px] shadow-sm">{saving ? "Guardando..." : "✨ Crear"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {tasks.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground"><CheckCircle2 className="h-10 w-10 mx-auto mb-2 opacity-50" /><p>No hay tareas pendientes</p></div>
        ) : (
          <div className="space-y-3">
            {tasks.map((task) => (
              <div key={task.id} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                <Checkbox checked={task.status === "completed"} onCheckedChange={() => toggleTaskComplete(task.id, task.status)} className="mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className={`font-medium ${task.status === "completed" ? "line-through text-muted-foreground" : "text-foreground"}`}>{task.title}</p>
                  {task.due_date && (
                    <span className={`flex items-center gap-1 mt-1 text-sm ${isOverdue(task.due_date) ? "text-destructive" : "text-muted-foreground"}`}>
                      <Clock className="h-3.5 w-3.5" />{formatDueDate(task.due_date)}{isOverdue(task.due_date) && " (Vencida)"}
                    </span>
                  )}
                </div>
                <Badge variant="outline" className={priorityColors[task.priority] || priorityColors.medium}>{priorityLabels[task.priority] || task.priority}</Badge>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
